import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Button,
  IconButton,
  Tooltip,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Snackbar,
  Switch,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Checkbox,
  useTheme,
  useMediaQuery,
  Chip,
  Card,
  CardContent,
  Divider,
  Tabs,
  Tab
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PeopleIcon from '@mui/icons-material/People';
import GetAppIcon from '@mui/icons-material/GetApp';
import { userApi } from '../../services/api';
import UserExportPanel from './UserExportPanel';

// Tab Panel Component
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`user-management-tabpanel-${index}`}
      aria-labelledby={`user-management-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `user-management-tab-${index}`,
    'aria-controls': `user-management-tabpanel-${index}`,
  };
}

// Consistent TextField styling
const textFieldStyles = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 1.5,
    fontSize: '1rem',
    height: '56px',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 1)',
    },
    '&.Mui-focused': {
      backgroundColor: 'rgba(255, 255, 255, 1)',
    }
  },
  '& .MuiInputLabel-root': {
    fontWeight: 500,
    fontSize: '1rem',
  },
  '& .MuiOutlinedInput-input': {
    fontSize: '1rem',
    lineHeight: 1.5
  },
  '& .MuiFormHelperText-root': {
    fontSize: '0.875rem',
    mt: 1
  }
};

const initialNewUserData = {
  username: '',
  email: '',
  first_name: '',
  last_name: '',
  password: '',
  confirm_password: '',
  role: '', // Changed from role_name to role, will store ID
  agency: '', // Should be agency ID
  is_active: true, // Default to true for new users
  direct_permissions: [], // For consistency, though likely set via Role or specific UI in edit mode
};

const UserManagementPanel = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Tab state
  const [tabValue, setTabValue] = useState(0);
  
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [usersError, setUsersError] = useState(null);

  const [openCreateUserDialog, setOpenCreateUserDialog] = useState(false);
  const [newUserData, setNewUserData] = useState(initialNewUserData);
  const [rolesList, setRolesList] = useState([]);
  const [agenciesList, setAgenciesList] = useState([]);
  const [allPermissionsList, setAllPermissionsList] = useState([]); // New state for all permissions
  const [createUserLoading, setCreateUserLoading] = useState(false);
  const [createUserError, setCreateUserError] = useState(null);

  const [openEditUserDialog, setOpenEditUserDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editUserData, setEditUserData] = useState({});
  const [editUserLoading, setEditUserLoading] = useState(false);
  const [editUserError, setEditUserError] = useState(null);

  // Delete User States
  const [openDeleteConfirmDialog, setOpenDeleteConfirmDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteUserLoading, setDeleteUserLoading] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  }, []);

  const fetchUsers = useCallback(async () => {
    setLoadingUsers(true);
    setUsersError(null);
    try {
      const response = await userApi.getAllUsers();
      setUsers(response.data?.results || response.data || []);
    } catch (error) {
      console.error("Error fetching users:", error);
      setUsersError(error.response?.data?.detail || 'Failed to fetch users.');
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  const fetchDropdownData = useCallback(async () => {
    try {
      const rolesResponse = await userApi.getRoles();
      console.log('rolesResponse.data:', rolesResponse.data); // <-- Debug log
      let rawRoles = [];
      if (rolesResponse.data && Array.isArray(rolesResponse.data.roles)) {
        rawRoles = rolesResponse.data.roles;
      } else if (rolesResponse.data && Array.isArray(rolesResponse.data.results)) {
        rawRoles = rolesResponse.data.results;
      } else if (Array.isArray(rolesResponse.data)) {
        rawRoles = rolesResponse.data;
      }
      console.log('rawRoles:', rawRoles); // <-- Debug log
      setRolesList(rawRoles.map(role => ({ 
        id: role.id, // Use actual role ID from database
        label: role.description || role.label, // Use description from roles-mng API
        value: role.name || role.value, // Use name from roles-mng API
        permissions: role.permissions ? role.permissions.map(p => p.id) : [] // Extract permission IDs
      })));

      const agenciesResponse = await userApi.getAgencies();
      setAgenciesList(agenciesResponse.data?.results || agenciesResponse.data || []);

      const permissionsResponse = await userApi.getPermissions();
      setAllPermissionsList(permissionsResponse.data?.results || permissionsResponse.data || []);

    } catch (error) {
      console.error("Error fetching dropdown data (roles, agencies, permissions):", error);
      showSnackbar('Failed to load auxiliary data for user forms.', 'error');
      setRolesList([]); 
      setAgenciesList([]);
      setAllPermissionsList([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSnackbar]); // showSnackbar is stable due to its own useCallback

  useEffect(() => {
    fetchUsers();
    fetchDropdownData();
  }, [fetchUsers, fetchDropdownData]);

  const handleOpenCreateUserDialog = () => {
    setNewUserData(initialNewUserData);
    setCreateUserError(null);
    setOpenCreateUserDialog(true);
  };

  const handleCloseCreateUserDialog = () => {
    setOpenCreateUserDialog(false);
  };

  const handleNewUserChange = (event) => {
    const { name, value, type, checked } = event.target;
    setNewUserData((prev) => ({ ...prev, [name]: type === 'checkbox' || type === 'switch' ? checked : value }));
  };

  const handleCreateUserSubmit = async (event) => {
    event.preventDefault();
    if (newUserData.password !== newUserData.confirm_password) {
      setCreateUserError("Passwords do not match.");
      return;
    }
    setCreateUserLoading(true);
    setCreateUserError(null);
    
    // Prepare payload
    const { confirm_password, ...dataToSend } = newUserData;
    // Find the selected role's name from rolesList
    let selectedRoleName = '';
    if (dataToSend.role && rolesList.length > 0) {
      const foundRole = rolesList.find(r => r.id === dataToSend.role || r.id === parseInt(dataToSend.role, 10));
      if (foundRole) {
        selectedRoleName = foundRole.value; // Use backend value only
      }
    }
    const payload = {
      ...dataToSend,
      user_key: dataToSend.username, // Add user_key, setting it to username
      is_active: !!dataToSend.is_active, // Ensure boolean
      direct_permissions: dataToSend.direct_permissions || [],
      role_name: selectedRoleName, // <-- Add role_name for backend
    };

    // Ensure agency is an integer or null
    if (payload.agency && typeof payload.agency === 'string') {
        payload.agency = parseInt(payload.agency, 10) || null;
    }
    // Ensure role is an integer or null if it's not empty
    if (payload.role && typeof payload.role === 'string') {
        payload.role = parseInt(payload.role, 10) || null;
    } else if (payload.role === '') {
        payload.role = null; // Explicitly set to null if empty string (e.g. "Select Role" was not changed)
    }

    console.log("Attempting to create user with payload:", payload);

    try {
      await userApi.createUser(payload);
      showSnackbar("User created successfully!", 'success');
      handleCloseCreateUserDialog();
      fetchUsers();
    } catch (error) {
      console.error("Error creating user:", error); 
      if (error.response) {
        console.error("Create User API Error Response:", error.response.data);
        const apiError = error.response.data;
        // Improved error message display
        let errorMessages = [];
        if (typeof apiError === 'string') {
          errorMessages.push(apiError);
        } else if (apiError && typeof apiError === 'object') {
          for (const key in apiError) {
            if (Array.isArray(apiError[key])) {
              errorMessages.push(`${key}: ${apiError[key].join(', ')}`);
            }
          }
        }
        setCreateUserError(errorMessages.length > 0 ? errorMessages.join('; ') : 'Failed to create user. Check server logs for details.');
      } else {
         setCreateUserError('Failed to create user. Network error or server unreachable.');
      }
    } finally { setCreateUserLoading(false); }
  };

  const handleOpenEditUserDialog = (user) => {
    setSelectedUser(user);
    
    // Find role ID from role_name
    let roleId = '';
    if (user.role_name && rolesList.length > 0) {
      const foundRole = rolesList.find(r => r.value === user.role_name);
      if (foundRole) {
        roleId = foundRole.id;
      }
    }
    
    setEditUserData({
      username: user.username || '',
      email: user.email || '',
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      role: roleId, // Use found role ID
      agency: user.agency || null, 
      is_active: user.is_active !== undefined ? user.is_active : true,
      direct_permissions: Array.isArray(user.direct_permissions) ? user.direct_permissions.map(p => parseInt(p.id || p, 10)).filter(id => !isNaN(id)) : [], // ensure array of integer IDs
      password: '',
      confirm_password: '',
    });
    setEditUserError(null);
    setOpenEditUserDialog(true);
  };

  const handleCloseEditUserDialog = () => setOpenEditUserDialog(false);

  const handleEditUserChange = (event) => {
    const { name, value, type, checked } = event.target;
    if (name.startsWith('permission_')) {
      const permissionId = parseInt(name.split('_')[1], 10);
      setEditUserData(prev => {
        const currentPermissions = prev.direct_permissions || [];
        let newPermissions;
        if (checked) {
          newPermissions = [...currentPermissions, permissionId];
        } else {
          newPermissions = currentPermissions.filter(id => id !== permissionId);
        }
        return { ...prev, direct_permissions: newPermissions };
      });
    } else {
      setEditUserData((prev) => ({ ...prev, [name]: type === 'checkbox' || type === 'switch' ? checked : value }));
    }
  };

  const handleEditUserSubmit = async (event) => {
    event.preventDefault();
    if (editUserData.password && editUserData.password !== editUserData.confirm_password) {
      setEditUserError("New passwords do not match.");
      return;
    }
    setEditUserLoading(true);
    setEditUserError(null);

    const { confirm_password, username, ...dataToSubmit } = editUserData;
    const payloadToSubmit = { ...dataToSubmit };

    if (!editUserData.password) {
      delete payloadToSubmit.password;
    }
    if (payloadToSubmit.agency && typeof payloadToSubmit.agency === 'string') {
        payloadToSubmit.agency = parseInt(payloadToSubmit.agency, 10) || null;
    }
    
    // Find the selected role's name from rolesList for role_name field
    let selectedRoleName = '';
    if (payloadToSubmit.role && rolesList.length > 0) {
      const foundRole = rolesList.find(r => r.id === payloadToSubmit.role || r.id === parseInt(payloadToSubmit.role, 10));
      if (foundRole) {
        selectedRoleName = foundRole.value; // Use backend value (SYSTEM_ADMIN, etc.)
      }
    }
    
    // Ensure role is an integer or null if it's not empty
    if (payloadToSubmit.role && typeof payloadToSubmit.role === 'string') {
        payloadToSubmit.role = parseInt(payloadToSubmit.role, 10) || null;
    } else if (payloadToSubmit.role === ''){
        payloadToSubmit.role = null;
    }
    
    // Add role_name for backend consistency
    payloadToSubmit.role_name = selectedRoleName;
    payloadToSubmit.direct_permissions = payloadToSubmit.direct_permissions || [];

    try {
      await userApi.updateUser(selectedUser.user_id || selectedUser.id, payloadToSubmit);
      showSnackbar("User updated successfully!", 'success');
      handleCloseEditUserDialog();
      fetchUsers(); 
    } catch (error) {
      console.error("Error updating user:", error.response?.data || error.message);
      const apiError = error.response?.data;
      let errorMessages = [];
      if (typeof apiError === 'string') {
        errorMessages.push(apiError);
      } else if (apiError && typeof apiError === 'object') {
        for (const key in apiError) {
          if (Array.isArray(apiError[key])) {
            errorMessages.push(`${key}: ${apiError[key].join(', ')}`);
          }
        }
      }
      setEditUserError(errorMessages.length > 0 ? errorMessages.join('; ') : 'Failed to update user.');
    } finally {
      setEditUserLoading(false);
    }
  };

  // Delete User Logic
  const handleOpenDeleteConfirmDialog = (user) => {
    setUserToDelete(user);
    setOpenDeleteConfirmDialog(true);
  };
  const handleCloseDeleteConfirmDialog = () => {
    setUserToDelete(null);
    setOpenDeleteConfirmDialog(false);
  };
  const handleConfirmDeleteUser = async () => {
    if (!userToDelete) return;
    setDeleteUserLoading(true);
    try {
      await userApi.deleteUser(userToDelete.user_id || userToDelete.id);
      showSnackbar("User deleted successfully!", 'success');
      handleCloseDeleteConfirmDialog();
      fetchUsers();
    } catch (error) {
      console.error("Error deleting user:", error.response?.data || error.message);
      showSnackbar(error.response?.data?.detail || 'Failed to delete user.', 'error');
    } finally {
      setDeleteUserLoading(false);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3,
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 2, sm: 0 }
      }}>
        <Box>
          <Typography variant="h6" component="h3" sx={{ 
            fontWeight: 600,
            fontSize: '1.25rem',
            color: 'text.primary'
          }}>
            User Management
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Manage user accounts, permissions, and export data
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Tooltip title="Refresh Users">
            <IconButton 
              onClick={fetchUsers} 
              disabled={loadingUsers || createUserLoading || editUserLoading || deleteUserLoading} 
              color="primary"
              sx={{ 
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreateUserDialog}
            disabled={createUserLoading || editUserLoading || deleteUserLoading}
            sx={{ 
              borderRadius: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 1.5
            }}
          >
            Add User
          </Button>
        </Box>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          aria-label="user management tabs"
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.95rem',
              minHeight: 48,
              px: 3
            }
          }}
        >
          <Tab 
            icon={<PeopleIcon />} 
            iconPosition="start"
            label="User Management" 
            {...a11yProps(0)} 
          />
          <Tab 
            icon={<GetAppIcon />} 
            iconPosition="start"
            label="Export Users" 
            {...a11yProps(1)} 
          />
        </Tabs>
      </Box>

      {/* Tab Panels */}
      <TabPanel value={tabValue} index={0}>
        {/* User Management Content */}
        <Box>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mb: 3,
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 2, sm: 0 }
          }}>
            <Box>
              <Typography variant="h6" component="h4" sx={{ 
                fontWeight: 600,
                fontSize: '1.1rem',
            color: 'text.primary'
          }}>
            Users ({users.length})
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Manage user accounts and permissions
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Tooltip title="Refresh Users">
            <IconButton 
              onClick={fetchUsers} 
              disabled={loadingUsers || createUserLoading || editUserLoading || deleteUserLoading} 
              color="primary"
              sx={{ 
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreateUserDialog}
            disabled={createUserLoading || editUserLoading || deleteUserLoading}
            sx={{ 
              borderRadius: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 1.5
            }}
          >
            Add User
          </Button>
        </Box>
      </Box>

      {loadingUsers && !usersError && ( // Show loader only if no error
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <CircularProgress />
        </Box>
      )}

      {usersError && (
        <Alert severity="error" sx={{ mb: 2 }} action={
            <Button color="inherit" size="small" onClick={fetchUsers}>
              TRY AGAIN
            </Button>
        }>
          {usersError}
        </Alert>
      )}

      {!loadingUsers && !usersError && (
        <Card elevation={0} sx={{ 
          border: '1px solid', 
          borderColor: 'divider', 
          borderRadius: 2,
          overflow: 'hidden'
        }}>
          <TableContainer sx={{ maxHeight: 600, overflowX: 'auto' }}>
            <Table stickyHeader aria-label="user management table">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ 
                    fontWeight: 600, 
                    backgroundColor: 'grey.50',
                    fontSize: '0.875rem'
                  }}>
                    Username
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 600, 
                    backgroundColor: 'grey.50',
                    fontSize: '0.875rem',
                    display: { xs: 'none', sm: 'table-cell' }
                  }}>
                    Full Name
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 600, 
                    backgroundColor: 'grey.50',
                    fontSize: '0.875rem'
                  }}>
                    Email
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 600, 
                    backgroundColor: 'grey.50',
                    fontSize: '0.875rem'
                  }}>
                    Role
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 600, 
                    backgroundColor: 'grey.50',
                    fontSize: '0.875rem',
                    display: { xs: 'none', md: 'table-cell' }
                  }}>
                    Agency
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 600, 
                    backgroundColor: 'grey.50',
                    fontSize: '0.875rem'
                  }} align="center">
                    Status
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 600, 
                    backgroundColor: 'grey.50',
                    fontSize: '0.875rem'
                  }} align="center">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No users found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((user) => (
                    <TableRow 
                      key={user.user_id || user.id} 
                      sx={{ 
                        '&:hover': { backgroundColor: 'action.hover' },
                        '& td': { borderBottom: '1px solid', borderColor: 'divider' }
                      }}
                    >
                      <TableCell component="th" scope="row" sx={{ fontWeight: 500 }}>
                        {user.username}
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        {user.full_name || '-'}
                      </TableCell>
                      <TableCell sx={{ 
                        maxWidth: 200, 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {user.email}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={(() => {
                            const foundRole = rolesList.find(r => r.value === user.role_name);
                            return foundRole ? foundRole.label : user.role_name;
                          })()} 
                          size="small" 
                          variant="outlined"
                          sx={{ 
                            borderRadius: 1,
                            fontSize: '0.75rem',
                            fontWeight: 500
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        {user.agency_name || 'N/A'}
                      </TableCell>
                      <TableCell align="center">
                        <Chip 
                          label={user.is_active ? 'Active' : 'Inactive'}
                          size="small"
                          color={user.is_active ? 'success' : 'default'}
                          sx={{ 
                            borderRadius: 1,
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            minWidth: 70
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          <Tooltip title="Edit User">
                            <IconButton 
                              size="small" 
                              onClick={() => handleOpenEditUserDialog(user)} 
                              color="primary" 
                              disabled={createUserLoading || editUserLoading || deleteUserLoading}
                              sx={{ borderRadius: 1 }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete User">
                            <IconButton 
                              size="small" 
                              onClick={() => handleOpenDeleteConfirmDialog(user)} 
                              color="error" 
                              disabled={createUserLoading || editUserLoading || deleteUserLoading}
                              sx={{ borderRadius: 1 }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {/* Create User Dialog */}
      <Dialog open={openCreateUserDialog} onClose={handleCloseCreateUserDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Create New User</DialogTitle>
        <Box component="form" onSubmit={handleCreateUserSubmit}>
          <DialogContent>
            <DialogContentText sx={{mb: 2}}>
              Enter the details for the new user.
            </DialogContentText>
            {createUserError && <Alert severity="error" sx={{ mb: 2 }}>{createUserError}</Alert>}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  autoFocus
                  required
                  margin="dense"
                  id="username"
                  name="username"
                  label="Username"
                  type="text"
                  fullWidth
                  variant="outlined"
                  size="medium"
                  value={newUserData.username}
                  onChange={handleNewUserChange}
                  sx={textFieldStyles}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  margin="dense"
                  id="email"
                  name="email"
                  label="Email Address"
                  type="email"
                  fullWidth
                  variant="outlined"
                  size="medium"
                  value={newUserData.email}
                  onChange={handleNewUserChange}
                  sx={textFieldStyles}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="dense"
                  id="first_name"
                  name="first_name"
                  label="First Name"
                  type="text"
                  fullWidth
                  variant="outlined"
                  size="medium"
                  value={newUserData.first_name}
                  onChange={handleNewUserChange}
                  sx={textFieldStyles}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="dense"
                  id="last_name"
                  name="last_name"
                  label="Last Name"
                  type="text"
                  fullWidth
                  variant="outlined"
                  size="medium"
                  value={newUserData.last_name}
                  onChange={handleNewUserChange}
                  sx={textFieldStyles}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  margin="dense"
                  id="password"
                  name="password"
                  label="Password"
                  type="password"
                  fullWidth
                  variant="outlined"
                  size="medium"
                  value={newUserData.password}
                  onChange={handleNewUserChange}
                  sx={textFieldStyles}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  margin="dense"
                  id="confirm_password"
                  name="confirm_password"
                  label="Confirm Password"
                  type="password"
                  fullWidth
                  variant="outlined"
                  size="medium"
                  value={newUserData.confirm_password}
                  onChange={handleNewUserChange}
                  error={newUserData.password !== newUserData.confirm_password && newUserData.confirm_password !== ''}
                  helperText={newUserData.password !== newUserData.confirm_password && newUserData.confirm_password !== '' ? "Passwords do not match" : ""}
                  sx={textFieldStyles}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="dense" required>
                  <InputLabel id="role-select-label" sx={{ fontWeight: 500, fontSize: '1rem' }}>
                    Role
                  </InputLabel>
                  <Select
                    labelId="role-select-label"
                    id="role"
                    name="role"
                    value={newUserData.role}
                    label="Role"
                    onChange={handleNewUserChange}
                    size="medium"
                    sx={{
                      borderRadius: 1.5,
                      height: '56px',
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 1)',
                      },
                      '&.Mui-focused': {
                        backgroundColor: 'rgba(255, 255, 255, 1)',
                      }
                    }}
                  >
                    <MenuItem value=""><em>Select Role</em></MenuItem>
                    {rolesList.map((role) => (
                      <MenuItem key={role.id} value={role.id}> 
                        {role.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="dense" required>
                  <InputLabel id="agency-select-label" sx={{ fontWeight: 500, fontSize: '1rem' }}>
                    Agency
                  </InputLabel>
                  <Select
                    labelId="agency-select-label"
                    id="agency"
                    name="agency"
                    value={newUserData.agency}
                    label="Agency"
                    onChange={handleNewUserChange}
                    size="medium"
                    sx={{
                      borderRadius: 1.5,
                      height: '56px',
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 1)',
                      },
                      '&.Mui-focused': {
                        backgroundColor: 'rgba(255, 255, 255, 1)',
                      }
                    }}
                  >
                    <MenuItem value=""><em>Select Agency</em></MenuItem>
                    {agenciesList.map((agency) => (
                      <MenuItem key={agency.agency_id} value={agency.agency_id}>
                        {agency.agency_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel control={<Switch checked={newUserData.is_active} onChange={handleNewUserChange} name="is_active" />} label="Active User" />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3, gap: 1 }}>
            <Button 
              onClick={handleCloseCreateUserDialog} 
              color="inherit"
              sx={{ 
                borderRadius: 1.5,
                textTransform: 'none',
                fontWeight: 500,
                px: 3,
                py: 1.5
              }}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              disabled={createUserLoading}
              sx={{ 
                borderRadius: 1.5,
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1.5,
                minWidth: 120
              }}
            >
              {createUserLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={18} color="inherit" />
                  Creating...
                </Box>
              ) : (
                "Create User"
              )}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      {/* Edit User Dialog */}
      {selectedUser && (
        <Dialog open={openEditUserDialog} onClose={handleCloseEditUserDialog} maxWidth="md" fullWidth>
          <DialogTitle sx={{ 
            fontSize: '1.5rem', 
            fontWeight: 600,
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}>
            Edit User: {editUserData.username}
          </DialogTitle>
          <Box component="form" onSubmit={handleEditUserSubmit}>
            <DialogContent sx={{ pt: 3 }}>
              <DialogContentText sx={{ mb: 3, fontSize: '1rem' }}>
                Update user details and permissions. Username cannot be changed.
              </DialogContentText>
              {editUserError && <Alert severity="error" sx={{ mb: 3 }}>{editUserError}</Alert>}
              
              {/* Basic Information Section */}
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
                Basic Information
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <TextField 
                    disabled 
                    margin="dense" 
                    id="edit_username" 
                    name="username" 
                    label="Username" 
                    type="text" 
                    fullWidth 
                    variant="outlined" 
                    size="medium"
                    value={editUserData.username}
                    sx={{
                      ...textFieldStyles,
                      '& .MuiOutlinedInput-root': {
                        ...textFieldStyles['& .MuiOutlinedInput-root'],
                        backgroundColor: 'rgba(0, 0, 0, 0.03)',
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField 
                    required 
                    margin="dense" 
                    id="edit_email" 
                    name="email" 
                    label="Email Address" 
                    type="email" 
                    fullWidth 
                    variant="outlined" 
                    size="medium"
                    value={editUserData.email} 
                    onChange={handleEditUserChange}
                    sx={textFieldStyles}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField 
                    margin="dense" 
                    id="edit_first_name" 
                    name="first_name" 
                    label="First Name" 
                    type="text" 
                    fullWidth 
                    variant="outlined" 
                    size="medium"
                    value={editUserData.first_name} 
                    onChange={handleEditUserChange}
                    sx={textFieldStyles}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField 
                    margin="dense" 
                    id="edit_last_name" 
                    name="last_name" 
                    label="Last Name" 
                    type="text" 
                    fullWidth 
                    variant="outlined" 
                    size="medium"
                    value={editUserData.last_name} 
                    onChange={handleEditUserChange}
                    sx={textFieldStyles}
                  />
                </Grid>
              </Grid>
              
              {/* Role & Agency Section */}
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
                Role & Agency
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth margin="dense" required>
                    <InputLabel id="edit-role-select-label" sx={{ fontWeight: 500, fontSize: '1rem' }}>
                      Role
                    </InputLabel>
                    <Select 
                      labelId="edit-role-select-label" 
                      id="edit_role"
                      name="role"
                      value={editUserData.role}
                      label="Role" 
                      onChange={handleEditUserChange}
                      size="medium"
                      sx={{
                        borderRadius: 1.5,
                        height: '56px',
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 1)',
                        },
                        '&.Mui-focused': {
                          backgroundColor: 'rgba(255, 255, 255, 1)',
                        }
                      }}
                    >
                      <MenuItem value=""><em>Select Role</em></MenuItem>
                      {rolesList.map((role) => (
                        <MenuItem key={role.id} value={role.id}>
                          {role.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth margin="dense" required>
                    <InputLabel id="edit-agency-select-label" sx={{ fontWeight: 500, fontSize: '1rem' }}>
                      Agency
                    </InputLabel>
                    <Select 
                      labelId="edit-agency-select-label" 
                      id="edit_agency" 
                      name="agency" 
                      value={editUserData.agency} 
                      label="Agency" 
                      onChange={handleEditUserChange}
                      size="medium"
                      sx={{
                        borderRadius: 1.5,
                        height: '56px',
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 1)',
                        },
                        '&.Mui-focused': {
                          backgroundColor: 'rgba(255, 255, 255, 1)',
                        }
                      }}
                    >
                      <MenuItem value=""><em>Select Agency</em></MenuItem>
                      {agenciesList.map((agency) => (
                        <MenuItem key={agency.agency_id} value={agency.agency_id}>
                          {agency.agency_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel 
                    control={
                      <Switch 
                        checked={editUserData.is_active} 
                        onChange={handleEditUserChange} 
                        name="is_active" 
                        color="primary"
                      />
                    } 
                    label="Active User"
                    sx={{ mt: 1 }}
                  />
                </Grid>
              </Grid>

              {/* Password Section */}
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
                Change Password (Optional)
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <TextField 
                    margin="dense" 
                    id="edit_password" 
                    name="password" 
                    label="New Password" 
                    type="password" 
                    fullWidth 
                    variant="outlined" 
                    size="medium"
                    value={editUserData.password} 
                    onChange={handleEditUserChange} 
                    helperText="Leave blank to keep current password"
                    sx={textFieldStyles}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField 
                    margin="dense" 
                    id="edit_confirm_password" 
                    name="confirm_password" 
                    label="Confirm New Password" 
                    type="password" 
                    fullWidth 
                    variant="outlined" 
                    size="medium"
                    value={editUserData.confirm_password} 
                    onChange={handleEditUserChange} 
                    error={editUserData.password !== editUserData.confirm_password && editUserData.confirm_password !== ''} 
                    helperText={editUserData.password !== editUserData.confirm_password && editUserData.confirm_password !== '' ? "Passwords do not match" : ""}
                    sx={textFieldStyles}
                  />
                </Grid>
              </Grid>
              
              {/* Permissions Section */}
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
                User Permissions ({editUserData.direct_permissions?.length || 0} direct + role permissions)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                ✓ = Assigned directly | 🔒 = From role ({editUserData.role ? rolesList.find(r => r.id === editUserData.role)?.label || 'Unknown' : 'No role'})
              </Typography>
              <Card variant="outlined" sx={{ 
                maxHeight: 300, 
                overflowY: 'auto', 
                borderRadius: 2,
                backgroundColor: 'grey.50'
              }}>
                <CardContent sx={{ p: 2 }}>
                  {allPermissionsList.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                      No permissions available or failed to load.
                    </Typography>
                  ) : (
                    <FormGroup>
                      {allPermissionsList.map((permission) => {
                        const hasDirectPermission = editUserData.direct_permissions?.includes(permission.id) || false;
                        
                        // Check if user has this permission from role
                        const userRole = editUserData.role ? rolesList.find(r => r.id === editUserData.role) : null;
                        const hasRolePermission = userRole && userRole.permissions && userRole.permissions.includes(permission.id);
                        
                        const isChecked = hasDirectPermission;
                        const isDisabledFromRole = hasRolePermission && !hasDirectPermission;
                        
                        return (
                          <FormControlLabel
                            key={permission.id}
                            control={
                              <Checkbox 
                                checked={isChecked || isDisabledFromRole}
                                onChange={handleEditUserChange} 
                                name={`permission_${permission.id}`} 
                                size="small"
                                disabled={isDisabledFromRole}
                                sx={{ 
                                  borderRadius: 1,
                                  '&.Mui-disabled': {
                                    color: 'action.disabled'
                                  }
                                }}
                              />
                            }
                            label={
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {permission.name} 
                                  {hasDirectPermission && <span style={{color: 'green'}}> ✓</span>}
                                  {isDisabledFromRole && <span style={{color: 'orange'}}> 🔒</span>}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {permission.category || 'General'} • {permission.description || permission.name}
                                  {isDisabledFromRole && ' (from role)'}
                                </Typography>
                              </Box>
                            }
                            sx={{ 
                              mb: 1,
                              p: 1,
                              borderRadius: 1,
                              backgroundColor: isDisabledFromRole ? 'action.hover' : 'transparent',
                              '&:hover': {
                                backgroundColor: isDisabledFromRole ? 'action.hover' : 'action.hover'
                              }
                            }}
                          />
                        );
                      })}
                    </FormGroup>
                  )}
                </CardContent>
              </Card>
            </DialogContent>
            <DialogActions sx={{ p: 3, gap: 1, borderTop: '1px solid', borderColor: 'divider' }}>
              <Button 
                onClick={handleCloseEditUserDialog} 
                color="inherit"
                sx={{ 
                  borderRadius: 1.5,
                  textTransform: 'none',
                  fontWeight: 500,
                  px: 3,
                  py: 1.5
                }}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                variant="contained" 
                disabled={editUserLoading}
                sx={{ 
                  borderRadius: 1.5,
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  py: 1.5,
                  minWidth: 120
                }}
              >
                {editUserLoading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={18} color="inherit" />
                    Saving...
                  </Box>
                ) : (
                  "Save Changes"
                )}
              </Button>
            </DialogActions>
          </Box>
        </Dialog>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteConfirmDialog}
        onClose={handleCloseDeleteConfirmDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {`Delete User: ${userToDelete?.username}?`}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this user? This action cannot be undone.
          </DialogContentText>
           {deleteUserLoading && <Box sx={{display: 'flex', justifyContent: 'center', my:2}}><CircularProgress size={24} /></Box>}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteConfirmDialog} color="inherit" disabled={deleteUserLoading}>Cancel</Button>
          <Button onClick={handleConfirmDeleteUser} color="error" variant="contained" disabled={deleteUserLoading} autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {/* User Export Content */}
        <UserExportPanel />
      </TabPanel>

      <Snackbar 
        open={snackbarOpen} 
        autoHideDuration={6000} 
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }} variant="filled">
          {snackbarMessage}
        </Alert>
      </Snackbar>

    </Box>
  );
};

export default UserManagementPanel; 